import type { MapProps } from '@vis.gl/react-google-maps';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef } from 'react';

import mapStyles from './mapStyles';

interface Props extends Omit<MapProps, 'center' | 'zoom'> {
  center?: google.maps.LatLngLiteral;
  zoom?: number;
  onMapLoad?: (map: google.maps.Map, maps: typeof google.maps) => void;
}

const MapLifecycle = ({
  center,
  zoom,
  onMapLoad
}: Pick<Props, 'center' | 'zoom' | 'onMapLoad'>) => {
  const map = useMap();
  const reportedMap = useRef<google.maps.Map | null>(null);
  const latitude = center?.lat;
  const longitude = center?.lng;

  useEffect(() => {
    if (map && zoom !== undefined) map.setZoom(zoom);
  }, [map, zoom]);

  useEffect(() => {
    if (!map || reportedMap.current === map) return;
    reportedMap.current = map;
    onMapLoad?.(map, window.google.maps);
  }, [map, onMapLoad]);

  // Synchronize changes from the form without controlling every camera movement.
  // fitBounds, zoom controls and manual panning can continue to use the native map.
  useEffect(() => {
    if (!map || latitude === undefined || longitude === undefined) return;
    map.panTo({ lat: latitude, lng: longitude });
  }, [map, latitude, longitude]);

  return null;
};

/** Must be rendered inside GoogleMapsApiBoundary. */
const GoogleMapsMap = ({ center, zoom, onMapLoad, children, ...props }: Props) => (
  <Map
    fullscreenControl={false}
    streetViewControl
    mapTypeControl
    clickableIcons={false}
    styles={mapStyles}
    defaultZoom={zoom}
    {...props}
  >
    <MapLifecycle center={center} zoom={zoom} onMapLoad={onMapLoad} />
    {children}
  </Map>
);

export default GoogleMapsMap;
